import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts and util
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';

// Shared components
import { Page, LayoutSingleColumn } from '../../components';

// Sibling containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

// Redux
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Category images, mapped by category id
import rentImage from './rent.jpg';
import saleImage from './sale.png';

import css from './ViewListingsPage.module.css';

const categoryImages = {
  rent: rentImage,
  sale: saleImage,
};

/**
 * The ViewListingsPage renders the marketplace categories as a row of centered
 * tabs. Categories are read from the hosted configuration via useConfiguration.
 *
 * @component
 * @returns {JSX.Element} View listings page component
 */
const ViewListingsPage = () => {
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const scrollingDisabled = useSelector(isScrollingDisabled);

  const categories = config.categoryConfiguration?.categories || [];

  const title = intl.formatMessage({ id: 'ViewListingsPage.title' });

  // Preserve any location that was selected before landing here (e.g. from the
  // SearchCTA), so it can be applied to the search once a category is chosen.
  const { address, bounds, origin } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const locationParams = {
    ...(address ? { address } : {}),
    ...(bounds ? { bounds } : {}),
    ...(origin ? { origin } : {}),
  };

  // Route the user to the search page for the clicked category, keeping any
  // preserved location params.
  const handleCategoryClick = category => {
    const queryParams = { pub_categoryLevel1: category.id, ...locationParams };
    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams);
    history.push(to);
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <h1 className={css.heading}>
            {intl.formatMessage({ id: 'ViewListingsPage.heading' })}
          </h1>
          <nav className={css.cards} aria-label={title}>
            {categories.map(category => {
              const image = categoryImages[category.id];
              return (
                <div
                  key={category.id}
                  className={css.card}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCategoryClick(category)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCategoryClick(category);
                    }
                  }}
                >
                  {image ? (
                    <img className={css.cardImage} src={image} alt={category.name} />
                  ) : null}
                  <span className={css.cardLabel}>{category.name}</span>
                </div>
              );
            })}
          </nav>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ViewListingsPage;
